import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PresetStatusColorType } from 'antd/lib/_util/colors';
import { Badge, Button, Form, Input, Select, Skeleton, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import { useAppDispatch, useAppSelector } from 'app/store';
import { uiModel } from 'entities/ui';
import { viewerModel } from 'entities/viewer';
import { getErrorData, getZupStatusName } from 'shared/helpers/utils';
import { usePath } from 'shared/hooks';
import { useNotifications } from 'shared/hooks/use-notifications';
import SuperviserApproveMarker from 'shared/components/superviserApproveMarker';
import { useIndicators } from 'features/indicators';
import { TPreparedVacationData, TPreparedVacationTableData } from 'shared/types/vacations.types';
import {
  ChangeResponsible,
  useChangeResponsibleVAMutation,
  useLazySingleVacationVAQuery,
} from 'app/services/edpApiV1/vacationApplication';
import { RolesUser } from 'shared/enums';
import { NavigatesAndAgreement } from '../navigatesAndAgreement';
import style from './index.module.scss';

/**
 * @page Vacation user
 */

const columns: ColumnsType<TPreparedVacationTableData> = [
  {
    title: '№',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Дата',
    dataIndex: 'created',
    key: 'created',
  },
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
  },
  {
    title: 'Вид отпуска',
    dataIndex: 'vacationType',
    key: 'vacationType',
  },
  {
    title: 'Тип заявления',
    dataIndex: 'type',
    key: 'type',
  },
  {
    title: 'Дата начала',
    dataIndex: 'startedAt',
    key: 'startedAt',
  },
  {
    title: 'Дата окончания',
    dataIndex: 'endedAt',
    key: 'endedAt',
  },
  {
    title: 'Дни',
    dataIndex: 'amountDays',
    key: 'amountDays',
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    render: (status: { type?: PresetStatusColorType; text: string; isSupervisorApproved: boolean }, record) => {
      return (
        <>
          <span>
            <Badge status={status.type} />
            {status.text}
          </span>
          {(() => {
            if ('zupRequestStatus' in record && record.zupRequestStatus) {
              return (
                <>
                  <br />
                  <span>
                    <Badge status={record.zupRequestStatus.type} />
                    {getZupStatusName(record.zupRequestStatus.type)}
                  </span>
                </>
              );
            }
          })()}
          <br />
          {!status.isSupervisorApproved && <SuperviserApproveMarker text="Не согласовано руководителем" />}
        </>
      );
    },
  },
];

const VacationUserVA = () => {
  const [dataVacations, setDataVacations] = useState<TPreparedVacationData[]>();
  const [btnHrVisible, setBtnHrVisible] = useState<boolean>(false);
  const [isBtnGroupVisible, setIsBtnGroupVisible] = useState<boolean>(true);
  // храню значения id ответственных Hr
  const [prevRespHrId, setPrevRespHrId] = useState<number | null>(null);
  const tableData = useRef<TPreparedVacationTableData[]>();
  const { id } = useParams();
  const path = usePath(2);
  const [form] = Form.useForm();
  const { role, id: profileId } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);
  const dispatch = useAppDispatch();
  const vacationHrId = dataVacations?.[0]?.hr?.id;
  const [changeResponsible, resultChangeResponsible] = useChangeResponsibleVAMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result?.error) }),
  });

  const {
    isSuccess: successChangeResponsible,
    isError: isErrorChangeResponsible,
    error: errorResponsible,
    reset: resetChangeResponsible,
  } = resultChangeResponsible;
  const [vacationTrigger, { data: vacationData, isLoading, isFetching, isSuccess }] = useLazySingleVacationVAQuery();

  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });

  const onChangeResponsible = (values: { hrId: number }) => {
    const { hrId } = values;
    const bodyRequest: ChangeResponsible = {
      requestId: Number(id),
      hrId,
      employeeId: Number(profileId),
    };
    changeResponsible(bodyRequest);
    // проверка на возможность принимать решения по отпуску: если id выбранного hr
    // не равен id авторизовавшегося пользователя, то и нечего 'согласовывать' или 'отклонять' отпуск
    if (hrId !== profileId) {
      dispatch(uiModel.Common.actions.blockHrActions(true));
    } else {
      dispatch(uiModel.Common.actions.blockHrActions(false));
    }
    setBtnHrVisible(false);
    // устанавливаю предыдущее значение id для сравнения, в 'selectHandler когда массив с данными о заявке пуст
    setPrevRespHrId(hrId);
  };

  useEffect(() => {
    if (id) {
      vacationTrigger({ employeeId: Number(profileId), requestId: +id });
    }
  }, [id, profileId]);

  useEffect(() => {
    if (profileId && vacationHrId) {
      if (profileId !== vacationHrId) {
        dispatch(uiModel.Common.actions.blockHrActions(true));
      }
    }
    return () => {
      dispatch(uiModel.Common.actions.blockHrActions(false));
    };
  }, []);

  // присваиваю id предидущему HR
  useEffect(() => {
    if (profileId) {
      setPrevRespHrId(profileId);
    }
  }, [profileId]);

  useEffect(() => {
    if (dataVacations && dataVacations?.length > 0) {
      form.setFields([
        {
          name: 'tabel',
          value: dataVacations[0]?.employee?.personnelNumber,
        },
        {
          name: 'division',
          value: dataVacations[0]?.employee?.division,
        },
        {
          name: 'position',
          value: dataVacations[0]?.employee?.position,
        },
        {
          name: 'hrId',
          value: dataVacations[0]?.hr?.id,
        },
        {
          name: 'oldDates',
          value: `${dataVacations[0]?.scheduleStartedOld
            ?.split('-')
            .reverse()
            .join('.')} - ${dataVacations[0]?.scheduleEndedOld?.split('-').reverse().join('.')}`,
        },
      ]);
    }
  }, [dataVacations, form]);

  useEffect(() => {
    if (successChangeResponsible) {
      setBtnHrVisible(false);
    }
  }, [successChangeResponsible, setBtnHrVisible]);

  useIndicators(isSuccess, isFetching);

  useNotifications(
    successChangeResponsible,
    isErrorChangeResponsible,
    'Ответственный изменён',
    errorResponsible?.message,
    resetChangeResponsible,
  );

  return (
    <div className={style.wrap}>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          {id && (
            <NavigatesAndAgreement
              id={id}
              tabKey="my"
              setIsBtnGroupVisible={setIsBtnGroupVisible}
              isBtnGroupVisible={isBtnGroupVisible}
            />
          )}

          <Table scroll={{ x: true }} dataSource={tableData.current} columns={columns} pagination={false} bordered />

          <Form form={form} layout="vertical" name="userInfo" className={style.userInfo} onFinish={onChangeResponsible}>
            <div className={style.groupItem}>
              <Form.Item name="tabel" label="Табельный номер" labelAlign="left" className={style.inputWrap}>
                <Input className="userInput" disabled />
              </Form.Item>
              <Form.Item name="division" label="Подразделение" labelAlign="left" className={style.inputWrap}>
                <Input className="userInput" disabled />
              </Form.Item>
            </div>

            <div className={style.groupItem}>
              <Form.Item name="position" label="Должность" labelAlign="left" className={style.inputWrap}>
                <Input className="userInput" disabled />
              </Form.Item>

              {allSubordinates && (
                <Form.Item
                  name="hrId"
                  label="Ответственный HR"
                  labelAlign="left"
                  className={style.inputWrap}
                  rules={[
                    {
                      validator: async (_, v) => {
                        if (v === prevRespHrId) return Promise.reject();
                      },
                      message: 'Выберите нового ответственного',
                    },
                  ]}
                >
                  <Select
                    placeholder="Выберите из списка"
                    disabled={path === 'archive' || (role !== RolesUser.HRDirector && role !== RolesUser.SecondLine)}
                  >
                    {allSubordinates.map(({ id, name, middleName, lastName }) => (
                      <Select.Option key={id} value={id}>
                        {`${lastName} ${name} ${middleName ?? ''}`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </div>

            <div className={style.groupItem}>
              {dataVacations && dataVacations[0]?.scheduleStartedOld && dataVacations[0]?.scheduleEndedOld && (
                <Form.Item name="oldDates" label="Старые даты отпуска" labelAlign="left" className={style.inputWrap}>
                  <Input className="userInput" disabled />
                </Form.Item>
              )}
            </div>

            {btnHrVisible && (
              <Form.Item labelAlign="left" className={style.btn}>
                <Button htmlType="submit" type="primary">
                  Сохранить
                </Button>
              </Form.Item>
            )}
          </Form>
        </>
      )}
    </div>
  );
};

export default VacationUserVA;
